import { useMemo } from 'react'
import {
  hasActiveDecay,
  getDecayStatus,
  type FamilyMasteryState,
} from '../../../mastery'
import { MODULES } from '../../../content'
import type { AppState } from '../../../app/types'
import { MS_PER_DAY } from '../../../config/constants'

export interface ModuleDecayInfo {
  moduleId: string
  staleCount: number
  fadingCount: number
  /** Oldest `lastSeen` timestamp across decaying families in this module. */
  oldestLastSeen: number | null
  /** Worst status among decaying families: `null` when no decay. */
  worstStatus: 'fading' | 'stale' | null
  /** Days since the oldest decaying family was last practised. */
  daysSince: number
}

export interface ChildDecayResult {
  /** Per-module decay summary, keyed by moduleId. */
  perModule: Record<string, ModuleDecayInfo>
  /** Total decay counts across all modules. */
  totals: {
    stale: number
    fading: number
  }
}

/**
 * Match a familyMastery key to a moduleId using the same heuristic that all
 * four parent-v2 callers used before consolidation. Kept as a local helper so
 * the rule is in one place.
 *
 * The heuristic accepts two patterns:
 *   1. `key.startsWith(moduleId + '-')`  — canonical
 *   2. `key.includes('-' + moduleId.toLowerCase() + '-')`  — legacy fallback
 */
function keyBelongsToModule(key: string, moduleId: string): boolean {
  return (
    key.startsWith(moduleId + '-') ||
    key.includes('-' + moduleId.toLowerCase() + '-')
  )
}

/**
 * Single source of truth for parent-portal decay calculations.
 *
 * Iterates the family-mastery map ONCE per render (memoized on its reference)
 * and returns per-module + total decay information. Replaces the duplicated
 * iteration logic previously inlined in HomePage, TipsPage, ComparePage, and
 * ModuleDetailDrawer.
 *
 * Audit ref: PARENT_PORTAL_UX_AUDIT_2026 §18.4 C-P-NEW-3 / F-RUN-NEW-6.
 */
export function useChildDecay(childData: AppState | null): ChildDecayResult {
  return useMemo(() => {
    const empty: ChildDecayResult = {
      perModule: {},
      totals: { stale: 0, fading: 0 },
    }
    if (!childData?.familyMastery) return empty

    const now = Date.now()
    const perModule: Record<string, ModuleDecayInfo> = {}
    let totalStale = 0
    let totalFading = 0

    // Pre-seed every module with a zero entry so callers can safely index.
    for (const module of MODULES) {
      perModule[module.id] = {
        moduleId: module.id,
        staleCount: 0,
        fadingCount: 0,
        oldestLastSeen: null,
        worstStatus: null,
        daysSince: 0,
      }
    }

    for (const [key, state] of Object.entries(childData.familyMastery)) {
      const fs = state as FamilyMasteryState
      if (!hasActiveDecay(fs, now)) continue
      const status = getDecayStatus(fs, now)
      if (status !== 'stale' && status !== 'fading') continue

      // Attribute this family to whichever module matches the key.
      for (const module of MODULES) {
        if (!keyBelongsToModule(key, module.id)) continue
        const entry = perModule[module.id]
        if (status === 'stale') {
          entry.staleCount++
          totalStale++
          entry.worstStatus = 'stale'
        } else {
          entry.fadingCount++
          totalFading++
          if (entry.worstStatus !== 'stale') entry.worstStatus = 'fading'
        }
        if (
          entry.oldestLastSeen === null ||
          fs.lastSeen < entry.oldestLastSeen
        ) {
          entry.oldestLastSeen = fs.lastSeen
        }
        break
      }
    }

    // Finalize daysSince per module.
    for (const moduleId in perModule) {
      const entry = perModule[moduleId]
      if (entry.oldestLastSeen !== null) {
        entry.daysSince = Math.floor((now - entry.oldestLastSeen) / MS_PER_DAY)
      }
    }

    return {
      perModule,
      totals: { stale: totalStale, fading: totalFading },
    }
  }, [childData?.familyMastery])
}
